import { observer } from 'mobx-react-lite'
import './index.scss'
import XImg from '@assets/svg/x.svg'
import UpdateIcon from '@assets/svg/update.svg'
import { Context } from '@/index'
import { useContext, useState, useRef, useEffect } from 'react'
import Select from 'react-select'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { SELECT_STYLES, PRICE_FOR_CHOICES, toastOptions } from '@shared/index'
import {
  createTask as createTaskApi,
  uploadFiles as uploadFilesApi,
  uploadUrls as uploadUrlsApi,
} from '@api/task-api'
import { AttachFilesAndLinks } from '@Components/AttachFilesAndLinks'
import { toast } from 'react-toastify'

// ----------------
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
// ----------------
const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Tapşırığın adını daxil edin')
    .min(6, 'Ad ən azı 6 simvoldan ibarət olmalıdır'),
  description: Yup.string()
    .required('Tapşırığın təsvirini daxil edin')
    .min(16, 'Təsvir ən azı 16 simvoldan ibarət olmalıdır'),
  category: Yup.object().required('Kateqoriya seçin'),
  price: Yup.number()
    .typeError('Rəqəm daxil edin')
    .required('Məbləği daxil edin')
    .min(1000, 'Büdcə ən azı 1000 rubl olmalıdır'),
  price_for: Yup.object().required('Tarif seçin'),
  deadline: Yup.number()
    .typeError('Rəqəm daxil edin')
    .required('Saat sayını daxil edin')
    .min(1, 'Müddət ən azı 1 saat olmalıdır'),
})

const formOptions = {
  resolver: yupResolver(validationSchema),
  shouldUnregister: true,
}

const CreateTaskPage = observer(({ callback = null }) => {
  const { modal, tasksCategoriesStore, programmingLanguagesStore, userStore } =
    useContext(Context)
  const [selectedProgrammingLanguages, setSelectedProgrammingLanguages] =
    useState([])
  const [isCustomDeadline, setIsCustomDeadline] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const {
    register,
    // unregister,
    // errors,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm(formOptions)
  const selectedFilesRef = useRef([])
  const selectedLinksRef = useRef([])
  const [isUploading, setIsUploading] = useState(false)
  const buttonSpinner = useRef(null)

  const watchedPrice = useWatch({ control, name: 'price' })

  useEffect(() => {
    if (isUploading) {
      setIsDisabled(true)
      buttonSpinner.current.style.display = 'inline-block'
    } else {
      setIsDisabled(false)
      buttonSpinner.current.style.display = 'none'
    }
  }, [isUploading])

  useEffect(() => {
    if (userStore.isFreelancer) {
      return
    }
    if (userStore.user.balance < watchedPrice) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }
  }, [watchedPrice, userStore.user.balance, userStore.isFreelancer])

  const onSubmit = (task) => {
    task.price_for = task.price_for.value
    task.category = task.category.id
    task.programming_language = selectedProgrammingLanguages
    task.task_type = userStore.isFreelancer ? 2 : 1
    setIsUploading(true)
    createTaskApi(task)
      .then(async (res) => {
        const [uploadTaskUrlsResult, uploadTaskFilesResult] =
          await Promise.allSettled([
            res && res.data && selectedLinksRef.current.length
              ? uploadUrlsApi({
                  task: res.data.id,
                  url: selectedLinksRef.current,
                })
              : Promise.resolve(),
            res && res.data && selectedFilesRef.current.length
              ? uploadFilesApi(res.data.id, selectedFilesRef.current)
              : Promise.resolve(),
          ])
        if (
          uploadTaskUrlsResult.status === 'fulfilled' &&
          uploadTaskFilesResult.status === 'fulfilled'
        ) {
          toast.success(
            `${
              task.task_type === 1 ? 'Sifariş yaradıldı' : 'Xidmət yaradıldı'
            } uğurla`,
            toastOptions
          )
        } else {
          toast.error(
            `${
              task.task_type === 1 ? 'Sifariş yaradıldı' : 'Xidmət yaradıldı'
            } xətalar ilə`,
            toastOptions
          )
        }
        if (uploadTaskUrlsResult.status === 'rejected') {
          toast.error(
            `${
              task.task_type === 1 ? 'Sifarişə' : 'Xidmətə'
            } link əlavə edilməsində xəta`,
            toastOptions
          )
        }
        if (uploadTaskFilesResult.status === 'rejected') {
          toast.error(
            `${
              task.task_type === 1 ? 'Sifarişə' : 'Xidmətə'
            } fayllar əlavə edilməsində xəta`,
            toastOptions
          )
        }
        setIsUploading(false)
        if (callback) {
          callback()
        }
        modal.closeModal()
      })
      .catch(() => {
        setIsUploading(false)
        if (callback) {
          callback()
        }
        toast.error(
          `${task.task_type === 1 ? 'Sifarişin' : 'Xidmətin'} yaradılmasında xəta`,
          toastOptions
        )
        modal.closeModal()
      })
  }

  const selectProgrammingLanguage = (pl) => {
    if (selectedProgrammingLanguages.includes(pl.id)) {
      let temp = selectedProgrammingLanguages.filter((v) => v !== pl.id)
      setSelectedProgrammingLanguages(temp)
    } else {
      setSelectedProgrammingLanguages((selectedProgrammingLanguages) => [
        ...selectedProgrammingLanguages,
        pl.id,
      ])
    }
  }

  const setIsCustomDeadlineHeandler = (e) => {
    setIsCustomDeadline(true)
  }

  const onFilesOrLinksChange = (files, links, isSizeLimit) => {
    selectedFilesRef.current = files
    selectedLinksRef.current = links
    setIsDisabled(isSizeLimit)
  }

  return (
    <div className="create-task-page">
      <div className="main">
        <div className="header-modal">
          {userStore.isFreelancer
            ? 'Yeni xidmət yaradın 😎'
            : 'Yeni tapşırıq yaradın 😎'}
          <div className="x-block" onClick={() => modal.closeModal()}>
            <img src={XImg} className="x" alt="close" />
          </div>
        </div>
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-control-block">
            <label htmlFor="title" className="form-label">
              {userStore.isFreelancer ? 'Xidmətin adı' : 'Sifarişin adı'}
            </label>
            <input
              type="text"
              className="form-control"
              id="title"
              placeholder={
                userStore.isFreelancer
                  ? 'Xidmətin adını daxil edin'
                  : 'Məsələn, saytın ana səhifəsinin dizaynını hazırlamaq...'
              }
              // {...register("title", { required: true })}/>
              {...register('title')}
            />
            {errors.title?.message && (
              <p className="error">{errors.title?.message}</p>
            )}
          </div>
          <div className="form-control-block">
            <label htmlFor="description" className="form-label">
              {userStore.isFreelancer
                ? 'Xidmətin təsviri'
                : 'Nə etmək lazımdır?'}
            </label>
            <textarea
              className="form-control"
              id="description"
              rows="4"
              placeholder={
                userStore.isFreelancer
                  ? 'Hansı xidməti göstərdiyinizi və Sifarişçinin nə əldə edəcəyini mümkün qədər ətraflı və dəqiq təsvir etməyə çalışın.'
                  : 'Məsələn, saytın ana səhifəsi üçün müasir, sadə və qavrayış üçün asan dizayn hazırlamaq lazımdır. Daha ətraflı texniki tapşırıq əlavədə...'
              }
              // {...register("description", { required: true })}></textarea>
              {...register('description')}
            ></textarea>
            {errors.description?.message && (
              <p className="error">{errors.description?.message}</p>
            )}
          </div>
          <AttachFilesAndLinks onChangeCallback={onFilesOrLinksChange} />
          <div className="form-control-block">
            <label htmlFor="task_category" className="form-label">
              {userStore.isFreelancer
                ? 'Xidmət hansı kateqoriyaya aiddir?'
                : 'Sifariş hansı kateqoriyaya aiddir?'}
            </label>
            <Controller
              name="category"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <Select
                  fullWidth
                  placeholder="Kateqoriya seçin"
                  styles={SELECT_STYLES}
                  options={tasksCategoriesStore.items}
                  getOptionLabel={(t) => t.name}
                  getOptionValue={(t) => t.id}
                  onChange={onChange}
                  onBlur={onBlur}
                  defaultValue={''}
                />
              )}
            />
            {errors.category?.message && (
              <p className="error">{errors.category?.message}</p>
            )}
          </div>
          <div className="form-control-block">
            <label htmlFor="task_category" className="form-label">
              Bir və ya bir neçə proqramlaşdırma dili seçin
            </label>
            <div className="selected-filters-block">
              {programmingLanguagesStore.items.map((pl) => {
                return (
                  <button
                    type="button"
                    key={pl.id}
                    className={
                      'button ' +
                      (selectedProgrammingLanguages.includes(pl.id)
                        ? 'blue'
                        : '')
                    }
                    onClick={() => selectProgrammingLanguage(pl)}
                  >
                    {pl.name}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="row">
            <div className={userStore.isFreelancer ? 'col-md-12' : 'col-md-6'}>
              <div className="form-control-block">
                <label className="form-label">
                  {userStore.isFreelancer
                    ? 'Xidmətin büdcəsini müəyyənləşdirin'
                    : 'Sifarişin büdcəsini müəyyənləşdirin'}
                </label>
                <div className="row">
                  <div className="col-5 padding-right-zero">
                    <Controller
                      control={control}
                      name="price"
                      defaultValue={''}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <input
                          type="number"
                          className="form-control"
                          placeholder="1000 AZN"
                          onChange={onChange}
                          ref={ref}
                          value={value}
                        />
                      )}
                    />
                    {errors.price?.message && (
                      <p className="error">{errors.price?.message}</p>
                    )}
                  </div>
                  <div className="col-2 vertically-middle padding-right-zero padding-left-zero">
üçün
                  </div>
                  <div className="col-5 padding-left-zero">
                    <Controller
                      name="price_for"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <Select
                          fullWidth
                          placeholder="Nə?"
                          styles={SELECT_STYLES}
                          options={PRICE_FOR_CHOICES}
                          defaultValue={''}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      )}
                    />
                    {errors.price_for?.message && (
                      <p className="error">{errors.price_for?.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {userStore.isFreelancer && setValue('deadline', 1)}
            {!userStore.isFreelancer && (
              <div className="col-md-6">
                <div className="form-control-block">
                  <label htmlFor="duration" className="form-label">
                    {userStore.isFreelancer
                      ? 'İcra müddətini göstərin'
                      : 'İstədiyiniz icra müddətini göstərin'}
                  </label>
                  {isCustomDeadline ? (
                    <div>
                      <input
                        type="number"
                        className="form-control"
                        defaultValue=""
                        placeholder="Saatla"
                        {...register('deadline')}
                      />
                    </div>
                  ) : (
                    <div
                      className="btn-group radio-group"
                      role="group"
                      aria-label="Basic radio toggle button group"
                    >
                      <input
                        type="radio"
                        className="btn-check"
                        name="btnradio"
                        id="btnradio1"
                        {...register('deadline')}
                        value="24"
                      />
                      <label
                        className="btn btn-outline-primary radio-button"
                        htmlFor="btnradio1"
                      >
                        24 saat
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="btnradio"
                        id="btnradio2"
                        {...register('deadline')}
                        value="72"
                      />
                      <label
                        className="btn btn-outline-primary radio-button"
                        htmlFor="btnradio2"
                      >
                        3 gün
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="btnradio"
                        id="btnradio3"
                        {...register('deadline')}
                        value="168"
                      />
                      <label
                        className="btn btn-outline-primary radio-button"
                        htmlFor="btnradio3"
                      >
                        7 gün
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="btnradio"
                        id="btnradio4"
                        {...register('deadline')}
                        value="1000"
                      />
                      <label
                        className="btn btn-outline-primary radio-button"
                        htmlFor="btnradio4"
                        onClick={setIsCustomDeadlineHeandler}
                      >
                        başqa
                      </label>
                    </div>
                  )}
                  {errors.deadline?.message && (
                    <p className="error">{errors.deadline?.message}</p>
                  )}
                </div>
              </div>
            )}
          </div>
          {userStore.user.balance < watchedPrice && (
            <div className="form-control-block">
              <div className="secure-transaction">
                <div className="up">
                  <h5 className="secure-transaction-title">
                    Sifariş rəsmiləşdirmək üçün kifayət qədər vəsait yoxdur 🧐
                  </h5>
                  <div className="secure-transaction-description">
                    Ödəniləcək məbləğ təhlükəsiz əməliyyatla sifariş yaradıldıqdan sonra dondurulur, buna görə də hesabınızda{' '}
                    <span className="bold">
                      ən azı{' '}
                      {new Intl.NumberFormat('ru-RU', {
                        style: 'currency',
                        currency: 'RUB',
                      }).format(watchedPrice)}{' '}
                    </span>
                    olmalıdır.
                  </div>
                </div>
                <div className="secure-transaction-separator"></div>
                <div className="down">
                  <div className="secure-transaction-description">
                    ⭐️ Balansınızda mövcuddur{' '}
                    <span className="bold">
                      {new Intl.NumberFormat('ru-RU', {
                        style: 'currency',
                        currency: 'RUB',
                      }).format(userStore.user.balance)}{' '}
                    </span>
                  </div>
                  <div className="buttons-transaction-separator">
                    <button
                      type="button"
                      className="my-button first-button"
                      onClick={() => {
                        userStore.fetchUser()
                      }}
                    >
                      <img src={UpdateIcon} alt="update balance" />
                    </button>
                    <a target="_blank" href="/edit-profile/3">
                      <button type="button" className="my-button first-button">
                        Balansı doldur
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="invalid-feedback">
            Zəhmət olmasa bütün sahələrin düzgünlüyünü yoxlayın
          </div>
          <div className="row">
            <div className="col-md-6">
              <button
                type="button"
                className="my-button second-button"
                onClick={() => modal.closeModal()}
              >
                Ləğv et
              </button>
            </div>
            <div className="col-md-6">
              <button
                type="submit"
                className="my-button first-button"
                disabled={isDisabled}
              >
                <span
                  ref={buttonSpinner}
                  className="button-custom-spinner spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                {userStore.isFreelancer ? 'Xidmət yaradın' : 'Sifariş yaradın'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
})

export { CreateTaskPage }
